/**
 * What the registration form holds, and what it refuses before sending.
 *
 * No state and no DOM, matching `notes/noteDraft.ts`: the screen reads these, so the checks are
 * testable without rendering and the rules are reviewable in one place.
 *
 * **The checks here are a second copy of the server's, deliberately, and one of them is a safety
 * property rather than a convenience.** `PatientRegistration.Validate` refuses the same things in
 * the same order, and it is the one that counts — a client check is never a guard (AGENTS.md §12).
 * But {@link looksLikeNationalNumber} is different in kind: a personnummer that the form refuses
 * **is never sent**, so it never reaches a request body, a server log or an `access-refused` audit
 * row. Phase 0 must never hold a real one (§6), and the cheapest way to honour that is for the
 * browser to stop it at the keyboard.
 *
 * The identifier format is a third statement of a rule whose authority is
 * `Continuum.TestData.ReservnummerSeries` — see `RegisteredIdentifier` on the server, which
 * documents the same situation. Three letters and eight digits, in all three places.
 */

/**
 * Longest accepted free-text value. Mirrors `PatientRegistration.MaxTextLength`.
 *
 * Not a database limit — the columns are `text`. A name of ten thousand characters is not a name,
 * and the field is reachable by any signed-in client.
 */
export const MAXIMUM_FIELD_LENGTH = 200

/** Letters in the block prefix. */
export const IDENTIFIER_PREFIX_LENGTH = 3

/** Digits in the sequence part. */
export const IDENTIFIER_SEQUENCE_LENGTH = 8

/** Total length of a well-formed reservnummer, e.g. `TST00000001`. */
export const IDENTIFIER_LENGTH = IDENTIFIER_PREFIX_LENGTH + IDENTIFIER_SEQUENCE_LENGTH

/**
 * The most characters the identifier **input** will hold — `19380303-9167`, twelve digits and a
 * separator, the longest thing {@link looksLikeNationalNumber} recognises.
 *
 * Deliberately not {@link IDENTIFIER_LENGTH}. It was, until 2026-10-07, and the browser pass found
 * what that did: a typed or pasted 12-digit personnummer was cut to eleven digits at the keyboard,
 * the national-number check never saw twelve, and the clinician was told the *format* was wrong
 * rather than that they were registering somebody who should be searched for. The number was still
 * never sent — malformed is refused too — but the one message written for that mistake was
 * unreachable for the commonest way of making it. The field has to be able to hold a value before
 * anything can recognise it; `isWellFormedIdentifier` still refuses everything that is not exactly
 * eleven.
 */
export const IDENTIFIER_INPUT_MAX_LENGTH = 13

/**
 * The sexes migration 0003's `patient_sex_known` allows, plus the empty choice.
 *
 * `unknown` and "not answered" are **different** and both are offered: the first is a fact
 * somebody established, the second is a field nobody filled in. The form defaults to the second
 * and supplies nothing on the clinician's behalf.
 */
export const OFFERED_SEXES = ['female', 'male', 'other', 'unknown'] as const

export type OfferedSex = (typeof OFFERED_SEXES)[number]

/** Every text field the form has, for the length check and for naming one in a message. */
export const REGISTRATION_FIELDS = [
  'identifier',
  'assigner',
  'givenName',
  'familyName',
  'birthDate',
  'bed',
] as const

export type RegistrationField = (typeof REGISTRATION_FIELDS)[number]

/**
 * The fields the length check applies to, **in the server's order**.
 *
 * Not all of {@link REGISTRATION_FIELDS}, and the two it leaves out are left out because
 * `PatientRegistration.Validate` leaves them out: `identifier` has already been refused for its
 * shape by the time the loop runs, and an over-long `birthDate` is a malformed date rather than a
 * long field. Iterating all six here would make the client report `REGISTRATION_FIELD_TOO_LONG`
 * where the server reports `REGISTRATION_BIRTH_DATE_MALFORMED` — one mistake, two names.
 *
 * The order is the server's tuple order, which is what decides *which* field a form with two
 * over-long values is told about.
 */
const LENGTH_CHECKED_FIELDS = ['givenName', 'familyName', 'assigner', 'bed'] as const

/**
 * The form, exactly as typed.
 *
 * Strings throughout, including the birth date — a `<input type="date">` gives a string and a
 * half-typed date is a state the draft has to be able to hold. Parsing happens in
 * {@link registrationProblem}, which is also where a malformed one becomes a message.
 */
export interface RegistrationDraft {
  readonly identifier: string
  readonly assigner: string
  readonly givenName: string
  readonly familyName: string

  /** ISO `YYYY-MM-DD`, or empty. */
  readonly birthDate: string

  /** One of {@link OFFERED_SEXES}, or empty for "not answered". */
  readonly sex: string

  readonly isEstimatedIdentity: boolean
  readonly bed: string
}

/**
 * A blank form.
 *
 * `assigner` starts empty rather than pre-filled with the ward's unit. A block is only unique if
 * somebody allocated it, and a default here would be this interface asserting who that was — the
 * same reason `Continuum.TestData` refuses to issue numbers without `--assigner` (AGENTS.md §10).
 */
export function emptyDraft(): RegistrationDraft {
  return {
    identifier: '',
    assigner: '',
    givenName: '',
    familyName: '',
    birthDate: '',
    sex: '',
    isEstimatedIdentity: false,
    bed: '',
  }
}

/** Whether anything has been typed, for the discard question. */
export function isDraftDirty(draft: RegistrationDraft): boolean {
  const blank = emptyDraft()

  return (
    Object.keys(blank) as readonly (keyof RegistrationDraft)[]
  ).some((field) => draft[field] !== blank[field])
}

/**
 * Trims, and upper-cases — so that what is checked and what is sent are the same string.
 *
 * Mirrors `RegisteredIdentifier.Normalise`. Inner whitespace is deliberately kept, so that
 * `TST 0000001` stays malformed rather than being silently repaired into a number the clinician
 * did not type: the one thing worse than refusing a typo is accepting a different patient's
 * number because of one.
 */
export function normaliseIdentifier(typed: string): string {
  return typed.trim().toUpperCase()
}

/** Whether a normalised value is shaped like a reservnummer from a pre-allocated block. */
export function isWellFormedIdentifier(candidate: string): boolean {
  if (candidate.length !== IDENTIFIER_LENGTH) {
    return false
  }

  // Character by character rather than by regex, because `\d` and `[A-Z]` under `u` are not the
  // question: `Å` is an upper-case letter and `٤` is a digit, and neither belongs in an
  // identifier (AGENTS.md §7). Explicit ASCII ranges say so.
  for (let index = 0; index < IDENTIFIER_LENGTH; index += 1) {
    const code = candidate.charCodeAt(index)
    const ascii =
      index < IDENTIFIER_PREFIX_LENGTH
        ? code >= 0x41 && code <= 0x5a // A–Z
        : code >= 0x30 && code <= 0x39 // 0–9

    if (!ascii) {
      return false
    }
  }

  return true
}

/**
 * Whether a normalised value is all digits and the length of a personnummer or a
 * samordningsnummer — ten or twelve, with or without a separator.
 *
 * Its own answer rather than falling through to "not a reservnummer", because the two mistakes
 * need different words: a clinician who typed a patient's personnummer has not made a formatting
 * error, they are trying to register somebody who should be *searched* for.
 */
export function looksLikeNationalNumber(candidate: string): boolean {
  if (candidate.length === 0) {
    return false
  }

  let digits = 0

  for (const character of candidate) {
    const code = character.charCodeAt(0)

    if (code >= 0x30 && code <= 0x39) {
      digits += 1
    } else if (character !== '-' && character !== '+') {
      // `+` separates the birth date from the sequence for a person over 100. Both separators are
      // accepted here only so the number is still *recognised* as one — it is refused either way.
      return false
    }
  }

  return digits === 10 || digits === 12
}

/**
 * A problem the form found, in the same shape a refusal arrives in.
 *
 * One code and one set of params, so the screen renders a client-side problem through exactly the
 * message the server would have sent. Two wordings for one mistake is how a clinician ends up
 * unable to tell which check they failed.
 */
export interface DraftProblem {
  readonly code:
    | 'REGISTRATION_IDENTIFIER_MISSING'
    | 'REGISTRATION_IDENTIFIER_IS_NATIONAL_NUMBER'
    | 'REGISTRATION_IDENTIFIER_MALFORMED'
    | 'REGISTRATION_ASSIGNER_MISSING'
    | 'REGISTRATION_FAMILY_NAME_MISSING'
    | 'REGISTRATION_FIELD_TOO_LONG'
    | 'REGISTRATION_SEX_UNKNOWN'
    | 'REGISTRATION_BIRTH_DATE_MALFORMED'
    | 'REGISTRATION_BIRTH_DATE_IN_FUTURE'
    | 'REGISTRATION_WARD_MISSING'

  readonly params: {
    readonly prefixLength?: number
    readonly sequenceLength?: number
    readonly field?: string
    readonly max?: number
    readonly sex?: string
    readonly birthDate?: string
  }
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * The first thing wrong with the draft, or null.
 *
 * **The order matches `PatientRegistration.Validate`'s**, which is what makes the two agree about
 * *which* problem a form with several has: the identifier first, because it is the field whose
 * being wrong matters most, and the screen shows one thing to fix at a time.
 *
 * @param draft The form as typed.
 * @param ward The ward the screen is registering onto, or null before the directory has answered.
 * @param today The clinical today, injected so the birth-date check is testable and so it agrees
 *   with the clock the record is written against.
 */
export function registrationProblem(
  draft: RegistrationDraft,
  ward: string | null,
  today: Date,
): DraftProblem | null {
  const identifier = normaliseIdentifier(draft.identifier)

  if (identifier.length === 0) {
    return { code: 'REGISTRATION_IDENTIFIER_MISSING', params: {} }
  }

  if (looksLikeNationalNumber(identifier)) {
    return { code: 'REGISTRATION_IDENTIFIER_IS_NATIONAL_NUMBER', params: {} }
  }

  if (!isWellFormedIdentifier(identifier)) {
    return {
      code: 'REGISTRATION_IDENTIFIER_MALFORMED',
      params: {
        prefixLength: IDENTIFIER_PREFIX_LENGTH,
        sequenceLength: IDENTIFIER_SEQUENCE_LENGTH,
      },
    }
  }

  if (draft.assigner.trim().length === 0) {
    return { code: 'REGISTRATION_ASSIGNER_MISSING', params: {} }
  }

  if (draft.familyName.trim().length === 0 && !draft.isEstimatedIdentity) {
    return { code: 'REGISTRATION_FAMILY_NAME_MISSING', params: {} }
  }

  for (const field of LENGTH_CHECKED_FIELDS) {
    if (draft[field].trim().length > MAXIMUM_FIELD_LENGTH) {
      return {
        code: 'REGISTRATION_FIELD_TOO_LONG',
        params: { field, max: MAXIMUM_FIELD_LENGTH },
      }
    }
  }

  if (
    draft.sex !== '' &&
    !(OFFERED_SEXES as readonly string[]).includes(draft.sex)
  ) {
    return { code: 'REGISTRATION_SEX_UNKNOWN', params: { sex: draft.sex } }
  }

  const birthDate = draft.birthDate.trim()

  if (birthDate !== '') {
    // Exact ISO, and then a real date. `Date.parse` alone would accept `1950-02-30` and quietly
    // move it to March, and a birth date that is silently wrong is visible nowhere on the screen
    // (I18N.md §3).
    if (!ISO_DATE.test(birthDate)) {
      return { code: 'REGISTRATION_BIRTH_DATE_MALFORMED', params: { birthDate } }
    }

    const parsed = new Date(`${birthDate}T00:00:00Z`)

    if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== birthDate) {
      return { code: 'REGISTRATION_BIRTH_DATE_MALFORMED', params: { birthDate } }
    }

    // Compared as calendar days in UTC, matching the server's `DateOnly` check. A birth date is a
    // calendar date rather than an instant, and comparing it against a local clock would refuse a
    // newborn registered today west of Greenwich.
    const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())

    if (parsed.getTime() > todayUtc) {
      return { code: 'REGISTRATION_BIRTH_DATE_IN_FUTURE', params: { birthDate } }
    }
  }

  if (ward === null || ward.trim().length === 0) {
    return { code: 'REGISTRATION_WARD_MISSING', params: {} }
  }

  return null
}

/**
 * The draft as the server wants it.
 *
 * Trims, and turns a field that is empty or only whitespace into `null` — a patient whose given
 * name is three spaces is a patient with no given name, and storing the spaces would make the
 * difference invisible on screen and visible in the `entry_hash`. The server does this again; the
 * point of doing it here is that what is checked above is what is sent.
 *
 * Call it only on a draft {@link registrationProblem} accepted: `identifier`, `assigner` and
 * `ward` are non-null here because that function has established they are non-empty.
 */
export function toSubmission(
  draft: RegistrationDraft,
  ward: string,
): {
  readonly identifier: string
  readonly assigner: string
  readonly givenName: string | null
  readonly familyName: string | null
  readonly birthDate: string | null
  readonly sex: string | null
  readonly isEstimatedIdentity: boolean
  readonly ward: string
  readonly bed: string | null
} {
  const orNull = (value: string): string | null => {
    const trimmed = value.trim()
    return trimmed.length === 0 ? null : trimmed
  }

  return {
    identifier: normaliseIdentifier(draft.identifier),
    assigner: draft.assigner.trim(),
    givenName: orNull(draft.givenName),
    familyName: orNull(draft.familyName),
    birthDate: orNull(draft.birthDate),
    sex: orNull(draft.sex),
    isEstimatedIdentity: draft.isEstimatedIdentity,
    ward: ward.trim().toUpperCase(),
    bed: orNull(draft.bed),
  }
}
